import { flow, initial, last, mapKeys } from "lodash";
import type { RouteRecordRaw, RouteRecordRedirectOption } from "vue-router";
import { home } from "./guards/home";

declare module "vue-router" {
  interface RouteMeta {
    authRequired?: boolean;
    roles?: Array<string>;
    privilege?: { resource: string; attribute: string };
  }
}

/**
 * For every folder in 'pages' match the file named as the folder eg pages/Wizard/Wizard.vue
 * The rest of the files are used in the page but need not be made available
 *
 * \\1 is equivalent to \1 in regex, matches the capturing group number 1
 */
const modules = import.meta.glob("@/pages/**/*.vue");
// Takes a path and returns a capitalized module name
const getModuleName = flow([(value, key) => key.split(/[/.]+/g), initial, last]);

// Makes a map of all the pages in the application
const pages = mapKeys(modules, getModuleName);

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: { name: "home" },
  },
  {
    path: "/home",
    name: "home",
    redirect: home as RouteRecordRedirectOption,
  },
  {
    path: "/login",
    name: "login",
    component: pages.Login,
  },
  // {
  //   path: "/agreement/:id",
  //   name: "agreement-detail",
  //   component: pages.AgreementDetail,
  // },
  // {
  //   path: "/privacy-terms",
  //   name: "privacy-terms",
  //   component: pages.PrivacyTerms,
  // },
  // {
  //   path: "/profile-suspended",
  //   name: "profile-suspended",
  //   component: pages.ProfileSuspended,
  // },
  {
    path: "/welcome",
    name: "welcome",
    component: pages.Welcome,
  },
  {
    path: "/logged-user-profile",
    name: "logged-user-profile",
    component: pages.LoggedUserProfile,
    meta: {
      authRequired: true,
    },
  },
  // {
  //   path: "/additional-agreement",
  //   name: "additional-agreement",
  //   component: pages.AdditionalAgreement,
  //   meta: {
  //     authRequired: true,
  //   },
  // },
  {
    path: "/administrator",
    component: pages.Administrator,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "administrator",
        component: () => import("@/pages/Administrator/AdministratorIndex.vue"),
      },
      {
        path: ":id",
        name: "administrator-details",
        component: () => import("@/pages/Administrator/AdministratorDetails.vue"),
      },
    ],
  },
  {
    path: "/administrator-dashboard",
    name: "administrator-dashboard",
    component: pages.AdministratorDashboard,
    meta: {
      authRequired: true,
      roles: ["Administrator"],
    },
  },
  {
    path: "/backofficeuser-dashboard",
    name: "backofficeuser-dashboard",
    component: pages.BackofficeUserDashboard,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
    },
  },
  // {
  //   path: "/finaluser",
  //   name: "finaluser",
  //   component: pages.FinalUser,
  // },
  // {
  //   path: "/professional",
  //   name: "professional",
  //   component: pages.Professional,
  // },
  {
    path: "/professional-dashboard",
    name: "professional-dashboard",
    component: pages.ProfessionalDashboard,
    meta: {
      authRequired: true,
      roles: ["Professional"],
    },
  },
  {
    path: "/appointment",
    component: pages.Appointment,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "appointment",
        component: () => import("@/pages/Appointment/AppointmentIndex.vue"),
      },
    ],
  },
  {
    path: "/notifications",
    component: pages.Notification,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "notifications",
        component: () => import("@/pages/Notification/NotificationIndex.vue"),
      },
    ],
  },
  // {
  //   path: "/finalUser-dashboard",
  //   name: "finalUser-dashboard",
  //   component: pages.FinaluserDashboard,
  //   meta: {
  //     authRequired: true,
  //   },
  // },
  {
    path: "/reports",
    component: pages.Reports,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "reports",
        component: () => import("@/pages/Reports/ReportsIndex.vue"),
      },
      {
        path: "info",
        name: "reports-info",
        component: () => import("@/pages/Reports/ReportsInfoCondominiums.vue"),
      },
      {
        path: ":id/create",
        name: "reports-create",
        component: () => import("@/pages/Reports/ReportsCreate.vue"),
      },
      {
        path: ":id",
        name: "reports-detail",
        component: () => import("@/pages/Reports/ReportsDetail.vue"),
      },
      {
        path: ":id/ticket/:ticketId",
        name: "reports-ticket-detail",
        component: () => import("@/pages/Reports/ReportsTicketDetail.vue"),
      },
    ],
  },
  {
    path: "/network",
    component: pages.Network,
    meta: {
      authRequired: true,
      roles: ["Administrator", "Professional"],
      requiredSubscriptionActive: true,
    },
    children: [
      {
        path: "",
        name: "network",
        component: () => import("@/pages/Network/NetworkDetails.vue"),
      },
      {
        path: "edit",
        name: "network-edit",
        component: () => import("@/pages/Network/NetworkEdit.vue"),
      },
    ],
  },
  // {
  //   path: "/verify-company",
  //   component: pages.VerifyCompany,
  //   meta: {
  //     authRequired: true,
  //     roles: ["BackofficeUser"],
  //   },
  //   children: [
  //     {
  //       path: "",
  //       name: "verify-company",
  //       component: () => import("@/pages/VerifyCompany/VerifyCompanyIndex.vue"),
  //     },
  //   ],
  // },
  {
    path: "/network-professional",
    component: pages.NetworkProfessional,
    meta: {
      authRequired: true,
      roles: ["Professional"],
    },
    children: [
      {
        path: "",
        name: "network-professional",
        component: () => import("@/pages/NetworkProfessional/NetworkIndex.vue"),
      },
    ],
  },
  {
    path: "/requestloqal",
    component: pages.RequestLoqal,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "requestloqal",
        component: () => import("@/pages/RequestLoqal/RequestLoqalIndex.vue"),
      },
    ]
  },
  {
    path: "/wrong-role",
    name: "wrong-role",
    component: pages.WrongRole,
  },
  {
    path: "/condominium",
    component: pages.Condominium,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "condominium",
        component: () => import("@/pages/Condominium/CondominiumIndex.vue"),
      },
      // {
      //   path: ":id/evaluation/:evaluationId",
      //   name: "condominium-evaluation",
      //   component: () => import("@/pages/Condominium/CondominiumEvaluation.vue"),
      // },
      {
        path: ":id",
        name: "condominium-details",
        component: () => import("@/pages/Condominium/CondominiumDetails.vue"),
      },
      {
        path: ":id/project/:projectId",
        name: "condominium-project",
        component: () => import("@/pages/Condominium/CondominiumProject.vue"),
      },
    ],
  },
  // {
  //   path: "/configurator",
  //   component: pages.Configurator,
  //   meta: {
  //     authRequired: true,
  //   },
  //   children: [
  //     {
  //       path: ":id",
  //       name: "configurator-details",
  //       component: () => import("@/pages/Configurator/ConfiguratorDetails.vue"),
  //     },
  //   ],
  // },
  // {
  //   path: "/configurator-result",
  //   component: pages.ConfiguratorResult,
  //   meta: {
  //     authRequired: true,
  //   },
  //   children: [
  //     {
  //       path: ":condominiumId/simulation/:id",
  //       name: "configurator-result",
  //       component: () => import("@/pages/ConfiguratorResult/ConfiguratorResultDetails.vue"),
  //     },
  //   ],
  // },
  // {
  //   path: "/configurator-preventive-request-complete/:id",
  //   name: "configurator-preventive-request-complete",
  //   component: pages.ConfiguratorPreventiveRequestComplete,
  // },
  {
    path: "/company",
    component: pages.Company,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "company",
        component: () => import("@/pages/Company/CompanyIndex.vue"),
      },
      {
        path: ":id",
        name: "company-details",
        component: () => import("@/pages/Company/CompanyDetails.vue"),
      },
      {
        path: ":id/condominium/:condominiumId/contract",
        name: "company-condominium-contract",
        component: () => import("@/pages/Company/CondominiumContracts.vue"),
      },
    ],
  },
  // {
  //   path: "/requestcfp-complete/:id",
  //   name: "requestcfp-complete",
  //   component: pages.CondominiumRequestCfpComplete,
  // },
  {
    path: "/condominium-register",
    component: pages.CondominiumRegister,
    meta: {
      authRequired: true,
      // requiredSubscriptionActive: true
    },
    children: [
      {
        path: "condominium-register/:id?",
        name: "condominium-register",
        component: () => import("@/pages/CondominiumRegister/CondominiumRegister.vue"),
      },
    ],
  },
  {
    path: "/assistance",
    component: pages.Assistance,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "assistance",
        component: () => import("@/pages/Assistance/AssistanceIndex.vue"),
      },
      {
        path: ":id",
        name: "assistance-detail",
        component: () => import("@/pages/Assistance/AssistanceDetail.vue"),
      },
      {
        path: ":id/assign-impresa",
        name: "assistance-assign-impresa",
        component: () => import("@/pages/Assistance/AssistanceAssignImpresa.vue"),
      },
    ],
  },
  {
    path: "/LandingOtp",
    
    component: pages.LandingOtp,
    meta: {
    },
    children: [
      {
        path: ":token",
        name: "landing-otp",
        component: () => import("@/pages/LandingOtp/LandingOtpIndex.vue"),
      },
    ],
  },
  // {
  //   path: "/property-evaluator",
  //   component: pages.PropertyEvaluator,
  //   meta: {
  //     authRequired: true,
  //     roles: ["Administrator"],
  //     requiredSubscriptionActive: true,
  //   },
  //   children: [
  //     {
  //       path: ":id",
  //       name: "property-evaluator-detail",
  //       component: () => import("@/pages/PropertyEvaluator/PropertyEvaluatorDetail.vue"),
  //     },
  //     {
  //       path: ":id/property/:propertyId",
  //       name: "property-evaluator-update",
  //       component: () => import("@/pages/PropertyEvaluator/PropertyEvaluatorDetail.vue"),
  //     },
  //   ],
  // },
  {
    path: "/timetable",
    name: "timetable",
    component: pages.Timetable,
    meta: {
      authRequired: true,
      roles: ["Administrator"],
    },
  },
  // {
  //   path: "/subscription",
  //   name: "subscription",
  //   component: pages.Subscription,
  //   meta: {
  //     authRequired: true,
  //     roles: ["Administrator"],
  //   },
  // },
  // {
  //   path: "/subscription-checkout",
  //   name: "subscription-checkout",
  //   component: pages.SubscriptionCheckout,
  //   meta: {
  //     authRequired: true,
  //   },
  // },
  // {
  //   path: "/meeting",
  //   component: pages.Meeting,
  //   meta: {
  //     authRequired: true,
  //     roles: ["Administrator"],
  //   },
  //   children: [
  //     {
  //       path: ":condominiumId/meeting/:meetingId",
  //       name: "meeting-detail",
  //       component: () => import("@/pages/Meeting/MeetingDetail.vue"),
  //     },
  //   ],
  // },
  // {
  //   path: "/subscription-payment",
  //   name: "subscription-payment",
  //   component: pages.SubscriptionPayment,
  //   meta: {
  //     authRequired: true,
  //   },
  // },
  {
    path: "/profile/:profileId/condominium/:condominiumId",
    name: "profile-detail",
    component: pages.Profile,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/carbonfootprint-report/:id",
    name: "carbonfootprint-report",
    component: pages.CarbonFootPrintReport,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/placeholder",
    name: "placeholder",
    component: pages.Placeholder,
  },
  // {
  //   path: "/centopiazze",
  //   name: "centopiazze",
  //   component: () => import("@/pages/HDWorld/Centopiazze.vue"),
  // },
  {
    path: "/cer",
    name: "cer",
    component: () => import("@/pages/HDWorld/Cer.vue"),
  },
  {
    path: "/ristrutturazione",
    name: "ristrutturazione",
    component: () => import("@/pages/HDWorld/Agoracasa.vue"),
  },
  {
    path: "/report-rokkwell",
    component: pages.ReportRokkwell,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "report-rokkwell",
        component: () => import("@/pages/ReportRokkwell/ReportRokkwellIndex.vue"),
      },
    ],
  },
  {
    path: "/report-rokkwell-bo",
    component: pages.ReportRokkwellBackoffice,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "",
        name: "report-rokkwell-bo",
        component: () =>
          import("@/pages/ReportRokkwellBackoffice/ReportRokkwellBackofficeIndex.vue"),
      },
      {
        path: ":id",
        name: "report-rokkwell-bo-detail",
        component: () =>
          import("@/pages/ReportRokkwellBackoffice/ReportRokkwellBackofficeDetail.vue"),
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: { name: "home" },
  },
];

// Add a dev page
if (import.meta.env.DEV) {
  routes.push({
    path: "/dev",
    name: "dev",
    component: pages.Dev,
  });
}

export default routes;
